import React from 'react';
import { FiUsers, FiDollarSign, FiStar, FiMapPin } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { formatTND, formatDistanceKm } from './utils/helpers';

const SearchStats = ({ results, radiusKm }) => {
  const stats = React.useMemo(() => {
    if (!results || results.length === 0) {
      return null;
    }

    const prices = results.map(r => r.price || 0).filter(p => p > 0);
    const totalPrice = prices.reduce((sum, p) => sum + p, 0);
    const totalRating = results.reduce((sum, r) => sum + (r.rating || 0), 0);
    const distances = results.map((r) => r.distance).filter((d) => d != null && Number.isFinite(d));
    const totalDistance = distances.reduce((sum, d) => sum + d, 0);

    return {
      averagePrice: prices.length > 0 ? totalPrice / prices.length : 0,
      averageRating: totalRating / results.length,
      totalMovers: results.length,
      averageDistance: distances.length > 0 ? totalDistance / distances.length : null,
      minPrice: prices.length > 0 ? Math.min(...prices) : 0,
      maxPrice: prices.length > 0 ? Math.max(...prices) : 0
    };
  }, [results]);

  if (!stats || stats.totalMovers === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-white mb-1">Résumé de votre recherche</h3>
          <p className="text-sm text-gray-400">{stats.totalMovers} déménageur{stats.totalMovers > 1 ? 's' : ''} trouvé{stats.totalMovers > 1 ? 's' : ''}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Nombre de déménageurs */}
        <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 rounded-xl p-4 border border-blue-500/20">
          <div className="flex items-center justify-between mb-2">
            <FiUsers className="w-5 h-5 text-blue-400" />
          </div>
          <div className="text-2xl font-bold text-white mb-1">{stats.totalMovers}</div>
          <div className="text-xs text-gray-400">Disponibles</div>
        </div>

        {/* Prix */}
        <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 rounded-xl p-4 border border-green-500/20">
          <div className="flex items-center justify-between mb-2">
            <FiDollarSign className="w-5 h-5 text-green-400" />
          </div>
          <div className="text-xl font-bold text-white mb-1">{formatTND(stats.averagePrice)}</div>
          <div className="text-xs text-gray-400">Prix moyen</div>
          {stats.minPrice > 0 && stats.maxPrice > 0 && (
            <div className="text-xs text-gray-500 mt-1">
              {formatTND(stats.minPrice)} - {formatTND(stats.maxPrice)}
            </div>
          )}
        </div>

        {/* Note */}
        <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 rounded-xl p-4 border border-amber-500/20">
          <div className="flex items-center justify-between mb-2">
            <FiStar className="w-5 h-5 text-amber-400 fill-amber-400" />
          </div>
          <div className="text-2xl font-bold text-white mb-1">
            {stats.averageRating.toFixed(1)}<span className="text-sm text-gray-400">/5</span>
          </div>
          <div className="text-xs text-gray-400">Note moyenne</div>
        </div>

        {/* Distance */}
        <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 rounded-xl p-4 border border-purple-500/20">
          <div className="flex items-center justify-between mb-2">
            <FiMapPin className="w-5 h-5 text-purple-400" />
          </div>
          <div className="text-2xl font-bold text-white mb-1">{formatDistanceKm(stats.averageDistance)}</div>
          <div className="text-xs text-gray-400">km en moyenne</div>
        </div>
      </div>
    </motion.div>
  );
};

export default SearchStats;
